import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { MapPin, NavigationArrow } from "@phosphor-icons/react";

export default function PincodeModal({ initial, onClose, onSave, suggestions = ["110034", "110085", "110052", "110086", "110088"] }) {
  const [pin, setPin] = useState(initial || "");
  const [detecting, setDetecting] = useState(false);

  const detectLocation = () => {
    if (!navigator.geolocation) return toast.error("Location not supported in this browser");
    setDetecting(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const r = await api.get("/customer/reverse-geocode", {
            params: { lat: latitude, lon: longitude },
          });
          if (r.data.pincode) {
            setPin(r.data.pincode);
            const where = r.data.locality || r.data.city || "your area";
            toast.success(`Detected: ${where} · ${r.data.pincode}`);
          } else {
            toast.error("Couldn't detect a pincode here. Please enter manually.");
          }
        } catch {
          toast.error("Couldn't detect pincode. Please enter manually.");
        }
        setDetecting(false);
      },
      (err) => {
        if (err.code === 1) toast.error("Location permission denied. Enter pincode manually.");
        else toast.error("Couldn't get location. Enter pincode manually.");
        setDetecting(false);
      },
      { timeout: 10000, enableHighAccuracy: false, maximumAge: 60000 }
    );
  };

  const save = (e) => {
    e?.preventDefault();
    if (pin.length !== 6) return toast.error("Enter a valid 6-digit pincode");
    onSave(pin);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4 fade-up" onClick={initial ? onClose : undefined}>
      <div className="bg-white w-full max-w-md max-h-[90vh] overflow-y-auto rounded-t-3xl sm:rounded-2xl shadow-2xl p-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="pincode-modal">
        <div className="text-center mb-5">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-[#F8CB46]/20 items-center justify-center mb-3">
            <MapPin weight="fill" size={28} className="text-[#15803D]" />
          </div>
          <h2 className="font-heading font-black text-2xl">Where do we deliver?</h2>
          <p className="text-sm text-gray-600 mt-1">Tell us your pincode to confirm delivery</p>
        </div>

        <button onClick={detectLocation} disabled={detecting}
          data-testid="pincode-detect-btn"
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-gray-200 hover:border-[#15803D] hover:bg-[#15803D]/5 transition font-semibold text-sm">
          <NavigationArrow weight={detecting ? "fill" : "bold"} size={18} className="text-[#15803D]" />
          {detecting ? "Detecting…" : "Detect my location"}
        </button>

        <div className="text-center text-xs text-gray-400 my-4 uppercase tracking-widest font-bold">or</div>

        <form onSubmit={save} className="space-y-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-gray-500">Pincode</label>
            <input
              data-testid="pincode-modal-input"
              type="tel" inputMode="numeric" maxLength={6} autoFocus
              className="qd-input mt-2 font-mono text-2xl tracking-[0.3em] text-center"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              placeholder="110034"
            />
          </div>
          {suggestions?.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {suggestions.map((p) => (
                <button type="button" key={p} onClick={() => setPin(p)}
                  className="text-xs px-2 py-1 rounded-full bg-gray-100 hover:bg-gray-200 font-mono"
                  data-testid={`pincode-suggest-${p}`}>
                  {p}
                </button>
              ))}
            </div>
          )}
          <button type="submit" className="qd-btn-green w-full" data-testid="pincode-modal-save">
            Continue
          </button>
          {initial && (
            <button type="button" onClick={onClose} className="text-xs text-gray-500 w-full text-center underline">
              Cancel
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
